import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../api";
import { useCart } from "../../context/CartContext";

const Products = () => {
  const { addItem } = useCart();
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCategory, setActiveCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(8);
  const [shuffledProducts, setShuffledProducts] = useState([]);

  const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          api.get("/products"),
          api.get("/categories"),
        ]);

        const products = productsRes.data?.data ?? productsRes.data;
        const cats = categoriesRes.data?.data ?? categoriesRes.data;

        setAllProducts(products);
        setShuffledProducts(shuffleArray(products));
        setCategories(["All", ...cats.map((c) => c.name)]);
      } catch (err) {
        setError("Could not load products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // resolve image: prefer imageUrl (CDN), fall back to image (local asset)
  const resolveImage = (product) => product.imageUrl || product.image || "";

  const filteredProducts =
    activeCategory === "All"
      ? shuffledProducts
      : allProducts.filter(
          (p) =>
            p.category?.name === activeCategory ||
            p.category === activeCategory
        );

  const visibleProducts = filteredProducts.slice(0, visibleCount);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    setVisibleCount(8);
    if (category === "All") setShuffledProducts(shuffleArray(allProducts));
  };

  const loadMore = () => setVisibleCount((prev) => prev + 8);

  return (
    <section
      className="w-full py-16 sm:py-24 bg-background-light dark:bg-background-dark/50"
      id="products"
    >
      <div className="px-4 md:px-10 lg:px-20 mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-[#181411] dark:text-white text-3xl sm:text-4xl font-bold leading-tight tracking-[-0.015em]">
            Our Products
          </h2>
          <p className="text-[#181411]/80 dark:text-white/80 mt-2 text-lg">
            Handcrafted with love and care.
          </p>
        </div>

        {/* Skeleton / error */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white dark:bg-background-dark rounded-xl overflow-hidden animate-pulse"
              >
                <div className="aspect-square w-full bg-gray-200 dark:bg-gray-700" />
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <p className="text-center text-red-500 py-12">{error}</p>
        )}

        {!loading && !error && (
          <>
            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryClick(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === category
                      ? "bg-primary text-white"
                      : "bg-white dark:bg-background-dark text-[#181411] dark:text-white hover:bg-primary/10"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Products Grid */}
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              layout
            >
              <AnimatePresence>
                {visibleProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white dark:bg-background-dark rounded-xl shadow-sm overflow-hidden flex flex-col group"
                  >
                    <div className="aspect-square w-full overflow-hidden relative">
                      <div
                        className="w-full h-full bg-center bg-no-repeat bg-cover group-hover:scale-105 transition-transform duration-300"
                        style={{ backgroundImage: `url(${resolveImage(product)})` }}
                      />
                      {product.available === false && (
                        <span className="absolute top-2 left-2 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded">
                          Out of stock
                        </span>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="text-xl font-bold text-[#181411] dark:text-white">
                        {product.name}
                      </h3>
                      <p className="text-[#181411]/70 dark:text-white/70 mt-2 flex-1">
                        {product.description}
                      </p>
                      <p className="text-lg font-semibold text-primary mt-4">
                        {Number(product.price).toFixed(2)} TND
                      </p>
                      <button
                        onClick={() => addItem(product)}
                        disabled={product.available === false}
                        className="w-full mt-3 flex items-center justify-center rounded-lg h-10 px-4 bg-primary/20 text-primary text-sm font-bold hover:bg-primary/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {product.available === false ? "Out of stock" : "Add to Cart"}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Load More */}
            {visibleCount < filteredProducts.length && (
              <div className="text-center mt-10">
                <button
                  onClick={loadMore}
                  className="px-6 py-3 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Products;
